import { redactQuery, redactText } from '../../../shared/redact';
import { srcPrefix } from '../config';
import { describeElement, elementPath, isOwn, onRecorderNav, onRecorderNet } from '../recorder';

function blockSrc(block: Element): string | undefined {
  const src = block.getAttribute('data-bug-src');
  return src ? `${srcPrefix()}${src}` : undefined;
}

/*
 * Session recording for the UX review: the report trace (clicks with coordinates and path, navigation, sampled scroll and
 * moves) plus what the metrics need — block and file of a click, whether the page changed within a second (`dom`), API
 * requests with durations. Field values and texts are never recorded; the service computes the metrics.
 */
export const UX_TRACE_FORMAT = 'ux-trace/1';
const MAX_EVENTS = 3000;
const SAMPLE_MS = { scroll: 250, drag: 250, move: 500 } as const;
const DOM_WINDOW_MS = 1000;

export interface UxEvent {
  t: number;
  type: 'click' | 'nav' | 'scroll' | 'move' | 'net';
  x?: number;
  y?: number;
  vw?: number;
  vh?: number;
  sx?: number;
  sy?: number;
  target?: string;
  path?: string;
  block?: string;
  src?: string;
  dom?: 0 | 1;
  b?: 1;
  method?: string;
  url?: string;
  status?: number | null;
  ms?: number;
}

export interface UxTraceFile {
  format: typeof UX_TRACE_FORMAT;
  startedAt: string;
  durationMs: number;
  viewport: { w: number; h: number };
  dropped: number;
  events: UxEvent[];
}

interface Session {
  start: number;
  startedIso: string;
  events: UxEvent[];
  dropped: number;
  lastAt: Map<keyof typeof SAMPLE_MS, number>;
  mutations: number;
  off: () => void;
}

let session: Session | null = null;

function push(s: Session, event: Omit<UxEvent, 't'>, at = performance.now()): UxEvent {
  const full: UxEvent = { t: Math.max(0, Math.round(at - s.start)), ...event };
  s.events.push(full);
  if (s.events.length > MAX_EVENTS) {
    const extra = s.events.length - MAX_EVENTS;
    s.events.splice(0, extra);
    s.dropped += extra;
  }
  return full;
}

function due(s: Session, kind: keyof typeof SAMPLE_MS, at: number): boolean {
  const last = s.lastAt.get(kind);
  if (last !== undefined && at - last < SAMPLE_MS[kind]) return false;
  s.lastAt.set(kind, at);
  return true;
}

const here = (): string => redactQuery(`${window.location.pathname}${window.location.search}`);

export function uxWatchActive(): boolean {
  return session !== null;
}

export function uxWatchStatus(): { events: number; durationMs: number } | null {
  return session ? { events: session.events.length, durationMs: Math.round(performance.now() - session.start) } : null;
}

export function startUxWatch(): void {
  if (session || typeof window === 'undefined') return;
  const s: Session = {
    start: performance.now(),
    startedIso: new Date().toISOString(),
    events: [],
    dropped: 0,
    lastAt: new Map(),
    mutations: 0,
    off: () => undefined,
  };
  push(s, { type: 'nav', path: here() });

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      const node = record.target;
      const el = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
      if (el && el.closest('[data-bug-own]')) continue;
      s.mutations += 1;
      return;
    }
  });
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, characterData: true });

  const onClick = (event: MouseEvent) => {
    if (isOwn(event.target)) return;
    const el = event.target instanceof Element ? event.target : null;
    const block = el?.closest('[data-bug-block]');
    const recorded = push(s, {
      type: 'click',
      x: Math.round(event.clientX),
      y: Math.round(event.clientY),
      vw: window.innerWidth,
      vh: window.innerHeight,
      target: redactText(describeElement(event.target)),
      path: redactText(elementPath(event.target)),
      ...(block ? { block: block.getAttribute('data-bug-block') ?? undefined, src: blockSrc(block) } : {}),
    });
    const before = s.mutations;
    window.setTimeout(() => {
      if (session === s) recorded.dom = s.mutations > before ? 1 : 0;
    }, DOM_WINDOW_MS);
  };
  const onMove = (event: MouseEvent) => {
    const pressed = event.buttons > 0;
    const at = performance.now();
    if (!due(s, pressed ? 'drag' : 'move', at) || isOwn(event.target)) return;
    push(s, { type: 'move', x: Math.round(event.clientX), y: Math.round(event.clientY), ...(pressed ? { b: 1 as const } : {}) }, at);
  };
  const onScroll = (event: Event) => {
    const at = performance.now();
    if (!due(s, 'scroll', at) || isOwn(event.target)) return;
    const el = event.target instanceof Element ? event.target : null;
    push(
      s,
      el
        ? { type: 'scroll', sx: Math.round(el.scrollLeft), sy: Math.round(el.scrollTop), target: redactText(describeElement(el)) }
        : { type: 'scroll', sx: Math.round(window.scrollX), sy: Math.round(window.scrollY) },
      at,
    );
  };
  document.addEventListener('click', onClick, { capture: true, passive: true });
  document.addEventListener('mousemove', onMove, { capture: true, passive: true });
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  const offNav = onRecorderNav((path) => push(s, { type: 'nav', path }));
  const offNet = onRecorderNet((entry, startedAt) => {
    if (startedAt < s.start || !entry.url.includes('/api/')) return;
    push(s, { type: 'net', method: entry.method, url: entry.url, status: entry.status, ms: entry.ms ?? undefined }, startedAt);
  });
  s.off = () => {
    observer.disconnect();
    document.removeEventListener('click', onClick, { capture: true });
    document.removeEventListener('mousemove', onMove, { capture: true });
    document.removeEventListener('scroll', onScroll, { capture: true });
    offNav();
    offNet();
  };
  session = s;
}

export function stopUxWatch(): UxTraceFile | null {
  const s = session;
  if (!s) return null;
  s.off();
  session = null;
  const events = s.events
    .map((event) => ({
      ...event,
      ...(event.target !== undefined ? { target: redactText(event.target) } : {}),
      ...(event.path !== undefined && event.type === 'click' ? { path: redactText(event.path) } : {}),
    }))
    .sort((a, b) => a.t - b.t);
  return {
    format: UX_TRACE_FORMAT,
    startedAt: s.startedIso,
    durationMs: Math.round(performance.now() - s.start),
    viewport: { w: window.innerWidth, h: window.innerHeight },
    dropped: s.dropped,
    events,
  };
}

export function mmss(ms: number): string {
  const sec = Math.floor(ms / 1000);
  return `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;
}
